import React, { useState, useEffect } from 'react';
import { Excalidraw } from '@excalidraw/excalidraw';
import '@excalidraw/excalidraw/index.css';
import Layout from '../components/Layout';
import WhiteboardList from '../components/WhiteboardList';
import Toast from '../components/Toast';
import WhiteboardAiChat from '../components/WhiteboardAiChat';
import { SavedWhiteboard, whiteboardService } from '../services/whiteboardService';
import '../styles/Pages.css';
import '../styles/Whiteboard.css';

const Whiteboard: React.FC = () => {
  const [showBoard, setShowBoard] = useState(false);
  const [currentWhiteboardId, setCurrentWhiteboardId] = useState<string | null>(null);
  const [whiteboardName, setWhiteboardName] = useState('Untitled Whiteboard');
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showNewBoardPopup, setShowNewBoardPopup] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [excalidrawAPI, setExcalidrawAPI] = useState<any>(null);
  const [initialData, setInitialData] = useState<any>(null);
  const [aiChatOpen, setAiChatOpen] = useState(false);

  // Set window name to enable same-tab library installation
  useEffect(() => {
    window.name = 'excalidraw-whiteboard';
  }, []);

  // Ensure close button exists in Help dialog
  useEffect(() => {
    const ensureCloseButton = () => {
      // Target all possible dialog containers and their content areas
      const dialogSelectors = [
        '.Dialog',
        '[role="dialog"]',
        '.excalidraw .Modal',
        '.Island.Dialog',
        '.excalidraw__dialog',
        'div[class*="Dialog"]'
      ];

      dialogSelectors.forEach(selector => {
        const dialogs = document.querySelectorAll(selector);

        dialogs.forEach((dialog) => {
          const dialogElement = dialog as HTMLElement;

          // Skip if already has close button
          const existingClose = dialogElement.querySelector('.custom-close-btn');
          if (existingClose) return;

          // Make sure dialog has relative positioning
          const computedStyle = window.getComputedStyle(dialogElement);
          if (computedStyle.position === 'static') {
            dialogElement.style.position = 'relative';
          }

          // Create close button
          const closeButton = document.createElement('button');
          closeButton.className = 'custom-close-btn';
          closeButton.setAttribute('aria-label', 'Close dialog');
          closeButton.setAttribute('type', 'button');
          closeButton.innerHTML = '✕';
          closeButton.style.cssText = `
            position: absolute !important;
            top: 12px !important;
            right: 12px !important;
            background: #f8f9fa !important;
            border: 1px solid #dee2e6 !important;
            border-radius: 4px !important;
            font-size: 20px !important;
            cursor: pointer !important;
            z-index: 999999 !important;
            width: 28px !important;
            height: 28px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0 !important;
            color: #495057 !important;
            font-weight: bold !important;
            line-height: 1 !important;
            transition: all 0.2s !important;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
          `;

          closeButton.onmouseover = () => {
            closeButton.style.background = '#e9ecef !important';
            closeButton.style.borderColor = '#adb5bd !important';
          };
          closeButton.onmouseout = () => {
            closeButton.style.background = '#f8f9fa !important';
            closeButton.style.borderColor = '#dee2e6 !important';
          };

          closeButton.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Send Escape key event
            const escapeEvent = new KeyboardEvent('keydown', {
              key: 'Escape',
              code: 'Escape',
              keyCode: 27,
              which: 27,
              bubbles: true,
              cancelable: true
            });
            document.dispatchEvent(escapeEvent);
            dialogElement.dispatchEvent(escapeEvent);

            // Also try removing the dialog
            setTimeout(() => {
              dialogElement.style.display = 'none';
              dialogElement.remove();
            }, 10);
          };

          // Append to dialog (not insert at beginning)
          dialogElement.appendChild(closeButton);
        });
      });
    };

    // Run immediately and then periodically
    ensureCloseButton();
    const interval = setInterval(ensureCloseButton, 200);
    return () => clearInterval(interval);
  }, []);

  // Load user's library items on mount
  useEffect(() => {
    const loadLibraryItems = async () => {
      try {
        const items = await whiteboardService.getLibraryItems();
        if (items.length > 0 && excalidrawAPI) {
          // Load library items into Excalidraw
          excalidrawAPI.updateLibrary({
            libraryItems: items,
          });
        }
      } catch (error) {
        console.error('Failed to load library items:', error);
      }
    };

    if (excalidrawAPI) {
      loadLibraryItems();
    }
  }, [excalidrawAPI]);

  // Auto-save effect
  useEffect(() => {
    if (currentWhiteboardId && excalidrawAPI && !isSaving) {
      const timeoutId = setTimeout(() => {
        handleSave(false);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excalidrawAPI?.getSceneElements()]);


  const handleSave = async (showNotification = true) => {
    if (!excalidrawAPI) return;

    try {
      setIsSaving(true);
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      // Image binary data lives in a separate files map keyed by fileId — it is
      // NOT part of `elements`. Persist only the files actually referenced by
      // current image elements (avoids storing orphaned/deleted images).
      const allFiles = excalidrawAPI.getFiles?.() ?? {};
      const usedFileIds = new Set(
        (elements as any[])
          .filter(el => el.type === 'image' && el.fileId && !el.isDeleted)
          .map(el => el.fileId),
      );
      const files: Record<string, unknown> = {};
      Object.keys(allFiles).forEach(id => { if (usedFileIds.has(id)) files[id] = allFiles[id]; });

      const whiteboardData = {
        elements: elements,
        files,
        appState: {
          viewBackgroundColor: appState.viewBackgroundColor,
          currentItemStrokeColor: appState.currentItemStrokeColor,
          currentItemBackgroundColor: appState.currentItemBackgroundColor,
          currentItemFillStyle: appState.currentItemFillStyle,
          currentItemStrokeWidth: appState.currentItemStrokeWidth,
          currentItemRoughness: appState.currentItemRoughness,
          currentItemOpacity: appState.currentItemOpacity,
          currentItemFontFamily: appState.currentItemFontFamily,
          currentItemFontSize: appState.currentItemFontSize,
          currentItemTextAlign: appState.currentItemTextAlign,
          currentItemStrokeStyle: appState.currentItemStrokeStyle,
          currentItemRoundness: appState.currentItemRoundness,
        }
      };

      if (currentWhiteboardId) {
        await whiteboardService.updateWhiteboard(currentWhiteboardId, {
          name: whiteboardName,
          elements: whiteboardData as any,
        });
      } else {
        const newBoard = await whiteboardService.createWhiteboard(whiteboardName, whiteboardData as any);
        setCurrentWhiteboardId(newBoard.id);
      }
      if (showNotification) {
        setToast({ message: 'Whiteboard saved successfully!', type: 'success' });
      }
    } catch (error) {
      console.error('Failed to save whiteboard:', error);
      if (showNotification) {
        setToast({ message: 'Failed to save whiteboard', type: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadWhiteboard = (whiteboard: SavedWhiteboard) => {
    setCurrentWhiteboardId(whiteboard.id);
    setWhiteboardName(whiteboard.name);

    // Load Excalidraw data
    if (whiteboard.elements) {
      const excalidrawData = whiteboard.elements as any;
      setInitialData({
        elements: excalidrawData.elements || [],
        appState: excalidrawData.appState || {},
        files: excalidrawData.files || {},
      });
    }

    setShowBoard(true);
    setShowNewBoardPopup(false);
    setNewBoardName('');
  };

  const handleNewBoard = () => {
    setNewBoardName('Untitled Whiteboard');
    setShowNewBoardPopup(true);
  };

  const handleNewBoardConfirm = () => {
    if (!newBoardName.trim()) return;
    setCurrentWhiteboardId(null);
    setWhiteboardName(newBoardName);
    setInitialData({ elements: [] });
    setShowBoard(true);
    setShowNewBoardPopup(false);
    setNewBoardName('');
  };

  const handleCancelNewBoard = () => {
    setShowNewBoardPopup(false);
    setNewBoardName('');
  };

  const handleBackToList = () => {
    if (excalidrawAPI && currentWhiteboardId) {
      handleSave(false);
    }
    setShowBoard(false);
    setCurrentWhiteboardId(null);
    setExcalidrawAPI(null);
    setInitialData(null);
  };

  if (!showBoard) {
    return (
      <Layout>
        <WhiteboardList
          onSelectWhiteboard={handleLoadWhiteboard}
          onNewBoard={handleNewBoard}
        />

        {/* New Board Popup */}
        {showNewBoardPopup && (
          <div className="modal-overlay" onClick={handleCancelNewBoard}>
            <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
              <h2>Create New Whiteboard</h2>
              <p>Enter a name for your new whiteboard:</p>
              <input
                type="text"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                placeholder="Whiteboard name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleNewBoardConfirm();
                  if (e.key === 'Escape') handleCancelNewBoard();
                }}
              />
              <div className="modal-actions">
                <button className="modal-btn-cancel" onClick={handleCancelNewBoard}>
                  Cancel
                </button>
                <button
                  className="modal-btn-primary"
                  onClick={handleNewBoardConfirm}
                  disabled={!newBoardName.trim()}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <>
      <Layout>
        <div className="whiteboard-container">
          <div className="whiteboard-header">
            <div className="whiteboard-title-section">
              <button className="back-button" onClick={handleBackToList} title="Back to list">
                ← Back
              </button>
              <input
                type="text"
                className="whiteboard-name-input"
                value={whiteboardName}
                onChange={(e) => setWhiteboardName(e.target.value)}
                placeholder="Whiteboard name"
              />
              {isSaving && <span className="saving-indicator">Saving...</span>}
            </div>
            <div className="whiteboard-header-actions">
              <button className="header-button save" onClick={() => handleSave(true)}>
                💾 Save
              </button>
              <button className="header-button" onClick={handleNewBoard}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                New Board
              </button>
            </div>
          </div>

          <div className="excalidraw-wrapper">
            <Excalidraw
              key={currentWhiteboardId || 'new'}
              excalidrawAPI={(api) => setExcalidrawAPI(api)}
              initialData={initialData || undefined}
              onChange={(elements, appState) => {
                // Auto-save trigger happens in useEffect
              }}
              onLibraryChange={async (items) => {
                // Save library items to database for user-level persistence
                try {
                  await whiteboardService.saveLibraryItems(items as any[]);
                } catch (error) {
                  console.error('Failed to save library items:', error);
                }
              }}
              libraryReturnUrl={window.location.origin + window.location.pathname}
              theme="light"
            />

            {!aiChatOpen && (
              <button
                className="wbai-fab"
                onClick={() => setAiChatOpen(true)}
                title="Draw with AI"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
                  <rect x="6" y="6" width="12" height="12" rx="3" />
                </svg>
                Ask AI
              </button>
            )}

            <WhiteboardAiChat
              excalidrawAPI={excalidrawAPI}
              open={aiChatOpen}
              onClose={() => setAiChatOpen(false)}
              // Persist AI edits like any manual change.
              onSceneChanged={() => { if (currentWhiteboardId) handleSave(false); }}
            />
          </div>

          {/* New Board Popup */}
          {showNewBoardPopup && (
            <div className="modal-overlay" onClick={handleCancelNewBoard}>
              <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
                <h2>Create New Whiteboard</h2>
                <p>Enter a name for your new whiteboard:</p>
                <input
                  type="text"
                  value={newBoardName}
                  onChange={(e) => setNewBoardName(e.target.value)}
                  placeholder="Whiteboard name"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleNewBoardConfirm();
                    if (e.key === 'Escape') handleCancelNewBoard();
                  }}
                />
                <div className="modal-actions">
                  <button className="modal-btn-cancel" onClick={handleCancelNewBoard}>
                    Cancel
                  </button>
                  <button
                    className="modal-btn-primary"
                    onClick={handleNewBoardConfirm}
                    disabled={!newBoardName.trim()}
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Whiteboard;
